import { withRentalProductOffer } from '../utilities/productStructuredData'
import React from 'react'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { BreadcrumbJsonLd, NextSeo } from 'next-seo'
import { useSelector } from 'react-redux'
import { wrapper } from '../store/configStore'
import { SERVER_IP } from '../store/middleware/api'
import { getFilteredProducts, productsReceived, productsSetFilters } from '../store/slices/entities/products'
import { getActiveUser } from '../store/slices/user'
import DJEquipmentRentalPage from '../components/design-system/marketing/DJEquipmentRentalPage'
import { djEquipmentRentalFallback } from '../content/seo/djEquipmentRental'
import { compactCatalogProducts } from '../utilities/catalogPublicPayload'
import { getDjSetupProducts } from '../utilities/djEquipmentCatalog'
import { compactPublicEvents } from '../utilities/eventPublicPayload'
import { helper } from '../utilities/helper'
import { compactSeoPublicPayload } from '../utilities/seoPublicPayload'
import { requestPageData } from '../utilities/boundedPageData'

const SeoPageDrawer = dynamic(() => import('../components/SeoPageDrawer'), { ssr: false })

const PAGE_PATH = '/dj-equipment-rental'

const getDjEquipmentSeoData = async ({ preview, previewData }) => {
  try {
    const response = await requestPageData({
      url: `${SERVER_IP}/seo/pages/${preview ? 'preview' : 'published'}`,
      label: 'SEO content',
      fallback: null,
      params: preview ? {
        path: PAGE_PATH,
        token: previewData?.seoPreviewToken
      } : {
        path: PAGE_PATH
      }
    })

    return compactSeoPublicPayload(response.data.data, {
      source: preview ? 'draft-preview' : 'published',
      customKeys: ['djEquipmentRental']
    })
  } catch (error) {
    console.warn(`Using source fallback for ${PAGE_PATH}: ${error.message}`)
    return compactSeoPublicPayload(djEquipmentRentalFallback, {
      source: 'source-fallback',
      customKeys: ['djEquipmentRental']
    })
  }
}

export const getStaticProps = wrapper.getStaticProps(
  async ({ store, preview, previewData }) => {
    const [landingResponse, productResponse, reviewResponse, seoData] = await Promise.all([
      requestPageData({
        url: `${SERVER_IP}/user/static-landing`,
        fallback: {
          newsletterevents: [],
          total_events: null
        },
        label: `landing proof for ${PAGE_PATH}`
      }),
      requestPageData({
        required: true,
        validateData: Array.isArray,
        url: `${SERVER_IP}/inventory/product`,
        params: {
          msr: true,
          cat: 'dj-equipment'
        },
        label: `products for ${PAGE_PATH}`
      }),
      requestPageData({
        url: `${SERVER_IP}/google-review/public`,
        params: {
          page: PAGE_PATH,
          limit: 3
        },
        fallback: null,
        label: `reviews for ${PAGE_PATH}`
      }),
      getDjEquipmentSeoData({ preview, previewData })
    ])

    const products = Array.isArray(productResponse.data) ? productResponse.data : []
    store.dispatch({ type: productsReceived.type, payload: compactCatalogProducts(products) })
    store.dispatch({ type: productsSetFilters.type, payload: { static: true } })

    const { newsletterevents = [], total_events = null } = landingResponse.data || {}
    const usedFallback = [landingResponse, productResponse, reviewResponse]
      .some(response => response.usedFallback) || seoData?.source === 'source-fallback'

    return {
      props: {
        newsletterevents: compactPublicEvents(newsletterevents),
        reviewData: reviewResponse.data,
        seoData,
        total_events
      },
      revalidate: usedFallback ? 60 : 3600
    }
  }
)

const mergeList = (fallbackSection, nextSection, key) => ({
  ...fallbackSection,
  ...(nextSection || {}),
  [key]: nextSection?.[key]?.length ? nextSection[key] : fallbackSection[key]
})

const mergeDjEquipmentContent = stored => {
  const fallback = djEquipmentRentalFallback.revision.content
  const next = stored?.custom?.djEquipmentRental || {}

  return {
    ...fallback,
    ...next,
    hero: { ...fallback.hero, ...(next.hero || {}) },
    process: mergeList(fallback.process, next.process, 'steps'),
    marketNotice: {
      ...fallback.marketNotice,
      ...(next.marketNotice || {}),
      cta: { ...fallback.marketNotice.cta, ...(next.marketNotice?.cta || {}) }
    },
    workflow: mergeList(fallback.workflow, next.workflow, 'items'),
    introduction: {
      ...fallback.introduction,
      ...(next.introduction || {}),
      paragraphs: next.introduction?.paragraphs?.length ? next.introduction.paragraphs : fallback.introduction.paragraphs,
      guideParagraphs: next.introduction?.guideParagraphs?.length ? next.introduction.guideParagraphs : fallback.introduction.guideParagraphs,
      signals: next.introduction?.signals?.length ? next.introduction.signals : fallback.introduction.signals
    },
    setups: { ...fallback.setups, ...(next.setups || {}) },
    specialists: mergeList(fallback.specialists, next.specialists, 'items'),
    components: { ...fallback.components, ...(next.components || {}) },
    boundary: {
      ...fallback.boundary,
      ...(next.boundary || {}),
      paragraphs: next.boundary?.paragraphs?.length ? next.boundary.paragraphs : fallback.boundary.paragraphs
    },
    events: { ...fallback.events, ...(next.events || {}) },
    reviews: { ...fallback.reviews, ...(next.reviews || {}) },
    faqs: next.faqs?.length ? next.faqs : fallback.faqs,
    related: mergeList(fallback.related, next.related, 'items'),
    finalCta: {
      ...fallback.finalCta,
      ...(next.finalCta || {}),
      proofItems: next.finalCta?.proofItems?.length ? next.finalCta.proofItems : fallback.finalCta.proofItems
    }
  }
}

const DJEquipmentRental = ({ newsletterevents, reviewData, seoData, total_events }) => {
  const products = useSelector(getFilteredProducts)
  const user = useSelector(getActiveUser)
  const page = seoData?.page || djEquipmentRentalFallback.page
  const revision = seoData?.revision || djEquipmentRentalFallback.revision
  const hasComponentContent = Boolean(revision.content?.custom?.djEquipmentRental)
  const seo = {
    ...djEquipmentRentalFallback.revision.seo,
    ...(hasComponentContent ? (revision.seo || {}) : {})
  }
  const content = mergeDjEquipmentContent(revision.content)
  const canonical = seo.canonicalUrl || page.canonicalUrl
  const setupProducts = getDjSetupProducts(products)
  const openGraph = {
    type: 'website',
    url: seo.openGraph?.url || canonical,
    title: seo.openGraph?.title || seo.title,
    description: seo.openGraph?.description || seo.description,
    images: seo.openGraph?.images || djEquipmentRentalFallback.revision.seo.openGraph.images,
    site_name: 'Miami Sound Rental'
  }
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${canonical}#service`,
    name: 'DJ Equipment Rental in Miami and South Florida',
    serviceType: 'Professional DJ equipment and configured DJ setup rental',
    url: canonical,
    description: seo.description,
    provider: { '@id': 'https://miamisoundrental.com/' },
    areaServed: [
      { '@type': 'City', name: 'Miami' },
      { '@type': 'City', name: 'Fort Lauderdale' },
      { '@type': 'City', name: 'West Palm Beach' },
      { '@type': 'AdministrativeArea', name: 'South Florida' }
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Complete DJ setup rental options',
      itemListElement: setupProducts.slice(0, 12).map(product => withRentalProductOffer({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Product',
          name: product.name,
          description: String(product.description || '').replace(/<[^>]*>/g, ' ').trim()
        },
        priceCurrency: 'USD',
        price: Number(product.price?.rental || 0),
        availability: 'https://schema.org/InStock',
        url: `https://miamisoundrental.com/products/${helper.makeUrl(product.name || '')}`
      })).filter(Boolean)
    }
  }

  return (
    <>
      <NextSeo
        title={seo.title}
        description={seo.description}
        canonical={canonical}
        noindex={seo.robots?.index === false}
        nofollow={seo.robots?.follow === false}
        additionalMetaTags={[{
          name: 'keywords',
          content: [seo.primaryKeyword, ...(seo.secondaryKeywords || [])].filter(Boolean).join(', ')
        }]}
        twitter={{
          handle: '@miamisoundrent1',
          site: '@miamisoundrent1',
          cardType: seo.twitter?.cardType || 'summary_large_image'
        }}
        openGraph={openGraph}
      />
      <BreadcrumbJsonLd itemListElements={[
        { position: 1, name: 'Home', item: 'https://miamisoundrental.com/' },
        { position: 2, name: 'DJ Equipment Rental', item: canonical }
      ]} />
      <Head>
        <script
          key='dj-equipment-rental-service-schema'
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
        />
      </Head>

      <DJEquipmentRentalPage
        content={content}
        newsletterevents={newsletterevents}
        products={products}
        reviewData={reviewData}
        totalEvents={total_events}
      />

      {user?.email === 'pedro@drynwet.com' && user?.employee && <SeoPageDrawer pagePath={PAGE_PATH} />}
    </>
  )
}

export default DJEquipmentRental
